import { useRef, useState } from 'react';
import { Box, Button, InlineStack, Stack, TextField } from '@customerio/pluma-components/react';
import { EMPTY_EVENT_DATA_FIELD, fieldsToJson, jsonToFields, type EventDataField } from '../utils/event-data';

/**
 * Event attributes as a key/value grid, for the common case of a handful of
 * plain values.
 *
 * The grid owns its rows rather than deriving them from the JSON it writes:
 * a row whose key has not been typed yet describes no attribute, so reading
 * the rows back out of the JSON would make it disappear under the cursor.
 */

type SimpleEventDataEditorProps = {
  /** The JSON the grid opens on. Read once — afterwards the grid is the source. */
  initialData: string;
  onChange: (data: string) => void;
  isDisabled?: boolean;
};

export function SimpleEventDataEditor({ initialData, onChange, isDisabled = false }: SimpleEventDataEditorProps) {
  const [fields, setFields] = useState<EventDataField[]>(() => jsonToFields(initialData));
  const lastKeyInput = useRef<HTMLInputElement | null>(null);

  const commit = (next: EventDataField[]) => {
    setFields(next);
    onChange(fieldsToJson(next));
  };

  const addField = () => {
    commit([...fields, { ...EMPTY_EVENT_DATA_FIELD }]);
    // The new row renders in the same commit, so focus after the browser has
    // painted it rather than on this one.
    requestAnimationFrame(() => lastKeyInput.current?.focus());
  };

  const removeField = (index: number) => {
    if (fields.length <= 1) {
      return;
    }
    commit(fields.filter((_, at) => at !== index));
  };

  const editField = (index: number, part: 'key' | 'value', value: string) => {
    // `asRead` goes with the edit: from here the text is the value, and the
    // rows nobody edited keep theirs.
    commit(
      fields.map((field, at) =>
        at === index
          ? {
              key: field.key,
              value: field.value,
              [part]: value,
            }
          : field,
      ),
    );
  };

  return (
    <Stack gap="150">
      {fields.map((field, index) => (
        // The rows have no identity of their own: a key is editable and can
        // repeat, so the position is what tells them apart.
        // oxlint-disable-next-line react/no-array-index-key -- rows are identified by position; keys are editable and may repeat
        <InlineStack key={index} gap="150" alignBlock="center">
          <Box flex={1} maxW="full" minW="0">
            {/* An attribute name can identify a person as readily as its
                value, so both are kept out of session replay. */}
            <TextField
              className="fs-mask"
              ariaLabel="Event attribute name"
              placeholder="Event attribute"
              value={field.key}
              isDisabled={isDisabled}
              ref={index === fields.length - 1 ? lastKeyInput : undefined}
              onInput={(event) => editField(index, 'key', event.target.value)}
            />
          </Box>
          <Box flex={1} maxW="full" minW="0">
            <TextField
              className="fs-mask"
              ariaLabel="Event attribute value"
              placeholder="Value"
              value={String(field.value)}
              isDisabled={isDisabled}
              onInput={(event) => editField(index, 'value', event.target.value)}
            />
          </Box>
          <Button
            variant="tertiary"
            icon="close"
            isIconOnly
            aria-label="Delete field"
            isDisabled={isDisabled || fields.length === 1}
            onClick={() => removeField(index)}
          />
        </InlineStack>
      ))}
      <div>
        <Button
          variant="secondary"
          size="sm"
          icon="add"
          iconPosition="trailing"
          isDisabled={isDisabled}
          onClick={addField}
        >
          Add attribute
        </Button>
      </div>
    </Stack>
  );
}
